/* Rosenhart — Generic modal infrastructure (Sprint 7.1)
 *
 * IIFE pattern (no ES module) for parity with the inline `{% javascript %}`
 * blocks used elsewhere in the theme — they all run in IIFE-style. Loaded
 * with `defer` from theme.liquid so DOM is ready when the script runs.
 *
 * Public API exposed on window:
 *   window.rhModal.open(id)   — open a modal by its DOM id
 *   window.rhModal.close(id)  — close (id optional; closes top modal if omitted)
 *
 * DOM contract:
 *   - Modal markup is a <dialog class="rh-modal" id="..." data-rh-modal>
 *     (rendered by the snippets/rh-modal.liquid snippet)
 *   - Trigger anywhere: <button data-rh-modal-open="{id}">…</button>
 *   - Close anywhere inside a modal: <button data-rh-modal-close>✕</button>
 *
 * Events dispatched on document:
 *   - 'rh:modal-opened' { detail: { id } }
 *   - 'rh:modal-closed' { detail: { id } }
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getModalById(id) {
    if (!id) return null;
    var el = document.getElementById(id);
    if (!el || !el.matches('dialog.rh-modal')) return null;
    return el;
  }

  function getOpenModal() {
    return document.querySelector('dialog.rh-modal[open]');
  }

  function focusFirstIn(modal) {
    var body = modal.querySelector('.rh-modal__body');
    var first = body && body.querySelector(FOCUSABLE_SELECTOR);
    if (first) {
      first.focus();
    } else {
      // Fallback to the close button so the modal is still keyboard-reachable.
      var closeBtn = modal.querySelector('.rh-modal__close');
      if (closeBtn) closeBtn.focus();
    }
  }

  function trapFocus(event, modal) {
    if (event.key !== 'Tab') return;
    var focusables = Array.prototype.slice.call(
      modal.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter(function (el) {
      // Exclude elements hidden via display: none (rare but safe)
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function open(id) {
    var modal = getModalById(id);
    if (!modal) return;
    if (modal.open) return; // already open
    modal.showModal();
    document.body.classList.add('rh-modal-open');
    focusFirstIn(modal);
    document.dispatchEvent(
      new CustomEvent('rh:modal-opened', { detail: { id: id } })
    );
  }

  function close(id) {
    var modal = id ? getModalById(id) : getOpenModal();
    if (!modal || !modal.open) return;
    var closedId = modal.id;
    modal.close();
    // Only release scroll lock if no other modal is still open
    if (!getOpenModal()) {
      document.body.classList.remove('rh-modal-open');
    }
    document.dispatchEvent(
      new CustomEvent('rh:modal-closed', { detail: { id: closedId } })
    );
  }

  // ---- Delegated event handlers (single listener for all triggers/closers) ----

  document.addEventListener('click', function (event) {
    // Open trigger?
    var opener = event.target.closest('[data-rh-modal-open]');
    if (opener) {
      event.preventDefault();
      var openId = opener.getAttribute('data-rh-modal-open');
      open(openId);
      return;
    }
    // Close trigger?
    var closer = event.target.closest('[data-rh-modal-close]');
    if (closer) {
      event.preventDefault();
      var hostModal = closer.closest('dialog.rh-modal');
      if (hostModal) close(hostModal.id);
    }
  });

  // ESC to close (the native <dialog> already does this via 'cancel', but we
  // intercept to also clear the body scroll lock and dispatch our event).
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openModal = getOpenModal();
    if (openModal) {
      // Prevent the default cancel→close sequence so we keep full control.
      event.preventDefault();
      close(openModal.id);
    }
  });

  // Focus trap: only active while a modal is open.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var openModal = getOpenModal();
    if (openModal) trapFocus(event, openModal);
  });

  // Public API
  window.rhModal = { open: open, close: close };
})();
